import { useState } from 'react'
import { Loader2, LogIn } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/components/providers/auth-provider'
import { apiErrorMessage, traceIdOf } from '@/lib/api-client'
import { TraceId } from '@/components/ui/trace-id'

export function LoginPage() {
  const { t } = useTranslation()
  const { login } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [traceId, setTraceId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await login(username, password)
    } catch (err) {
      setError(apiErrorMessage(err))
      setTraceId(traceIdOf(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-card p-6 shadow-sm"
      >
        <div>
          <h1 className="text-lg font-semibold">{t('app.title')}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">{t('app.tagline')}</p>
        </div>

        <div className="space-y-2">
          <label className="block text-xs font-medium text-muted-foreground">
            {t('auth.username')}
            <Input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
              className="mt-1 h-9"
            />
          </label>
          <label className="block text-xs font-medium text-muted-foreground">
            {t('auth.password')}
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="mt-1 h-9"
            />
          </label>
        </div>

        {error && (
          <p className="rounded-md border border-danger/30 bg-danger/10 p-2 text-xs text-danger">
            {error}
            {traceId && <TraceId id={traceId} className="ml-1" />}
          </p>
        )}

        <Button type="submit" size="lg" className="w-full" disabled={busy}>
          {busy ? <Loader2 className="animate-spin" /> : <LogIn />}
          {t('auth.signIn')}
        </Button>
      </form>
    </div>
  )
}
